import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PiCube, PiChartPie } from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import type { BrandBreakdown, OrderProductCard } from '../types';
import { contractBadgeClass, formatBrl } from '../utils';
import DealFulfillmentToolbar from './DealFulfillmentToolbar';
import DetailCard from './DetailCard';
import PloomesDealBrandChart from './PloomesDealBrandChart';
import PloomesDealProductCard from './PloomesDealProductCard';

/** Lotes progressivos: mostra na mesma página e busca o próximo automaticamente. */
const PER_PAGE = 50;

type ProductsPayload = {
    order_products: OrderProductCard[];
    order_products_count: number;
    order_products_total: number;
    brand_breakdown: BrandBreakdown | null;
    fulfillment_summary: { from_stock: number; to_import: number; total: number; pending_stock?: number };
    meta: { page: number; per_page: number; last_page: number; total: number };
};

type Props = {
    dealId: number;
    contractTypeLabel: string | null | undefined;
    productsCountHint?: number;
    canRequestStock: boolean;
};

export default function PloomesDealProductsSection({
    dealId,
    contractTypeLabel,
    productsCountHint = 0,
    canRequestStock,
}: Props) {
    const {
        data,
        isPending,
        isFetchingNextPage,
        hasNextPage,
        isError,
        fetchNextPage,
        refetch,
    } = useInfiniteQuery({
        queryKey: ['ploomes-deal-products', dealId, PER_PAGE],
        enabled: Boolean(dealId),
        staleTime: 60_000,
        initialPageParam: 1,
        queryFn: async ({ pageParam, signal }) => {
            const { data: body } = await api.get<{ data: ProductsPayload }>(
                `/ploomes-deals/${dealId}/products`,
                {
                    signal,
                    timeout: 60_000,
                    params: { page: pageParam, per_page: PER_PAGE },
                },
            );
            return body.data;
        },
        getNextPageParam: (last) => {
            const { page, last_page } = last.meta;
            return page < last_page ? page + 1 : undefined;
        },
    });

    // Encadeia os lotes: assim que um termina, pede o próximo até acabar.
    useEffect(() => {
        if (!hasNextPage || isFetchingNextPage || isError) {
            return;
        }
        void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage, data?.pages.length]);

    const products = useMemo(
        () => data?.pages.flatMap((p) => p.order_products) ?? [],
        [data?.pages],
    );

    const firstPage = data?.pages[0];
    const count = firstPage?.order_products_count ?? productsCountHint;
    const totalAmount = firstPage?.order_products_total ?? 0;
    const brand = data?.pages.find((p) => p.brand_breakdown)?.brand_breakdown ?? null;
    const isLoadingMore = Boolean(hasNextPage || isFetchingNextPage);

    const productsTitle = (
        <span className="inline-flex flex-wrap items-center gap-2">
            Produtos do Pedido ({count})
            {contractTypeLabel ? (
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${contractBadgeClass(contractTypeLabel)}`}>
                    {contractTypeLabel}
                </span>
            ) : (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">—</span>
            )}
        </span>
    );

    return (
        <div className="grid items-start gap-4 sm:gap-5 lg:grid-cols-12 lg:gap-5 xl:gap-6">
            {/*
              Produtos ~75–80% · Marcas sidebar estreita com donuts empilhados.
              Grade: 1 → 2 → 3 → 4 conforme a largura real da coluna.
            */}
            <div className="min-w-0 lg:col-span-9">
                <DetailCard title={productsTitle} icon={<PiCube className="h-5 w-5" />}>
                    {isPending && products.length === 0 && <LoadingState variant="cards" />}
                    {isError && products.length === 0 && (
                        <div className="space-y-2">
                            <p className="text-sm text-gray-500 dark:text-zinc-400">Não foi possível carregar os produtos.</p>
                            <button
                                type="button"
                                className="text-sm font-medium text-sky-600 hover:underline"
                                onClick={() => {
                                    void refetch();
                                }}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    )}
                    {!isPending && !isError && products.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-zinc-400">Nenhum produto vinculado.</p>
                    )}
                    {products.length > 0 && (
                        <>
                            <DealFulfillmentToolbar dealId={dealId} products={products} />
                            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:gap-4 xl:grid-cols-3 3xl:grid-cols-4 5xl:grid-cols-5">
                                {products.map((p) => (
                                    <PloomesDealProductCard
                                        key={p.id}
                                        product={p}
                                        dealId={dealId}
                                        canRequestStock={canRequestStock}
                                    />
                                ))}
                            </div>
                            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-1">
                                <span className="text-xs text-gray-500 dark:text-zinc-400">
                                    {isLoadingMore
                                        ? `Carregando ${products.length} de ${count}…`
                                        : `${products.length} produtos carregados`}
                                </span>
                                <p className="text-sm font-bold text-gray-900 dark:text-zinc-100">
                                    Total: {formatBrl(totalAmount)}
                                </p>
                            </div>
                            {isLoadingMore && (
                                <div className="mt-3">
                                    <LoadingState variant="inline" />
                                </div>
                            )}
                            {isError && hasNextPage && (
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        className="text-sm font-medium text-sky-600 hover:underline"
                                        onClick={() => {
                                            void fetchNextPage();
                                        }}
                                    >
                                        Continuar carregando
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </DetailCard>
            </div>

            <div className="min-w-0 lg:col-span-3 lg:sticky lg:top-16 lg:self-start">
                <DetailCard title="Marcas do Pedido" icon={<PiChartPie className="h-5 w-5" />}>
                    {isPending && !brand && <LoadingState variant="cards" />}
                    {brand && <PloomesDealBrandChart data={brand} />}
                    {!isPending && !brand && (
                        <p className="text-sm text-gray-500 dark:text-zinc-400">
                            Não foi possível identificar marcas nos produtos deste pedido.
                        </p>
                    )}
                </DetailCard>
            </div>
        </div>
    );
}
